import { forwardRef, type ComponentRef } from "react";

import { useWindowDimensions } from "@luce/core";

import { cn, platformProps } from "../../lib";
import { markPendingAware } from "../../lib/pending";
import { Pressable } from "../../primitives";
import { buttonSpinnerSize, buttonVariants } from "../button";
import { buttonHitSlop } from "../button/lib";
import { Icon } from "../icons";
import { Spinner } from "../spinner";
import {
  getIconButtonAccessibilityLabel,
  getIconButtonAccessibilityState,
  iconButtonIconColor,
  iconButtonIconSize,
  iconButtonIconStateClass,
  iconButtonSpinnerColorFor,
} from "./lib";
import type { IconButtonProps } from "./types";

export const IconButton = forwardRef<ComponentRef<typeof Pressable>, IconButtonProps>(
  (
    {
      icon,
      iconWeight,
      variant = "primary",
      size = "md",
      loading = false,
      className,
      testID,
      ...pressableProps
    },
    ref,
  ) => {
    const {
      onClick,
      disabled = false,
      accessibilityLabel,
      accessibilityState,
      hitSlop = buttonHitSlop[size],
      pending = false,
      ...restPressableProps
    } = pressableProps;

    const { isDesktop } = useWindowDimensions();
    const iconPx = iconButtonIconSize[size][isDesktop ? "desktop" : "mobile"];

    const accessibilityProps = {
      accessibilityLabel,
      accessibilityState,
      icon,
      loading,
      disabled,
    };

    const resolvedAccessibilityLabel = getIconButtonAccessibilityLabel(accessibilityProps);
    const mergedAccessibilityState = getIconButtonAccessibilityState(accessibilityProps);

    const buttonClassName = cn(
      buttonVariants({ variant, size, iconOnly: true, loading }),
      className,
    );

    const color = disabled ? "disabled" : "default";
    const iconColorClass = iconButtonIconColor[variant][color];
    const iconStateClass = !disabled ? iconButtonIconStateClass[variant] : undefined;

    const renderContent = () => {
      if (loading) {
        return (
          <Spinner
            size={buttonSpinnerSize[size]}
            color={iconButtonSpinnerColorFor(variant, disabled)}
            decorative
            className="shrink-0"
          />
        );
      }

      return (
        <Icon
          name={icon}
          size={iconPx}
          weight={iconWeight}
          className={cn("shrink-0", iconColorClass, iconStateClass)}
        />
      );
    };

    return (
      <Pressable
        ref={ref}
        {...restPressableProps}
        {...platformProps({ testID, slot: "icon-button" })}
        accessibilityRole="button"
        accessibilityLabel={resolvedAccessibilityLabel}
        accessibilityState={mergedAccessibilityState}
        disabled={disabled}
        // Loading ignores every activation, not only onClick
        pending={pending || loading}
        hitSlop={hitSlop}
        onClick={onClick}
        className={buttonClassName}
      >
        {renderContent()}
      </Pressable>
    );
  },
);

IconButton.displayName = "IconButton";
markPendingAware(IconButton);
